import { faExpand, faTrash, faXmark } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  ActionIcon,
  Badge,
  Divider,
  Flex,
  Group,
  Image,
  Modal,
  Paper,
  Stack,
  Text,
  Title,
  UnstyledButton,
} from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { modals } from '@mantine/modals'
import { useState } from 'react'
import { AllLocationsBundle } from '../../models/Checkout/AllLocationsBundle'
import { COLORS } from '../../utils/constants'
import { getAllLocationsDurationLabel } from '../../utils/displayUtils'

interface AllLocationsBundleCardProps {
  bundle: AllLocationsBundle
  onRemove: () => void
}

const getDurationText = (bundle: AllLocationsBundle): string => {
  if (bundle.numberOfDays) {
    return getAllLocationsDurationLabel(bundle.numberOfDays)
  }
  if (bundle.startDateLocal && bundle.endDateLocal) {
    return `${new Date(bundle.startDateLocal).toLocaleDateString()} – ${new Date(bundle.endDateLocal).toLocaleDateString()}`
  }
  return 'Custom duration'
}

function AllLocationsBundleCard({ bundle, onRemove }: AllLocationsBundleCardProps) {
  const [imageExpanded, setImageExpanded] = useState(false)
  const isMobile = useMediaQuery('(max-width: 768px)')

  const handleRemoveClick = () => {
    modals.openConfirmModal({
      title: 'Remove All Locations Bundle',
      children: (
        <Text size='sm'>
          Are you sure you want to remove <strong>{bundle.name}</strong> from
          your cart? This will remove the All Locations bundle booking.
        </Text>
      ),
      labels: { confirm: 'Remove', cancel: 'Cancel' },
      confirmProps: { style: { background: COLORS.HiddenGemzRed } },
      onConfirm: onRemove,
    })
  }

  return (
    <>
      <Paper withBorder shadow='md' w='100%' radius={20} p={16} style={{ overflow: 'hidden' }}>
        <Flex
          gap={16}
          direction={isMobile ? 'column' : 'row'}
          wrap='nowrap'
          align='flex-start'
        >
          {/* 16:9 image — left on desktop, stacked on top on mobile */}
          {bundle.imageUrl && (
            <div style={{ flexShrink: 0, width: isMobile ? '100%' : 300, aspectRatio: '16 / 9', borderRadius: 10, overflow: 'hidden', position: 'relative' }}>
              <Image
                src={bundle.imageUrl}
                alt={bundle.name}
                w='100%'
                h='100%'
                fit='cover'
              />
              <ActionIcon
                variant='filled'
                radius='md'
                onClick={() => setImageExpanded(true)}
                style={{
                  position: 'absolute',
                  bottom: 8,
                  right: 8,
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  width: 32,
                  height: 32,
                }}
              >
                <FontAwesomeIcon icon={faExpand} style={{ fontSize: 14, color: 'white' }} />
              </ActionIcon>
            </div>
          )}

          {/* Content — right of image on desktop, below it on mobile */}
          <Stack gap={10} style={{ flex: 1, minWidth: 0, width: isMobile ? '100%' : undefined }}>
            <Group justify='space-between' align='flex-start' wrap='nowrap'>
              <Stack gap={4}>
                <Group gap={8} wrap='wrap'>
                  <Title size='h4'>{bundle.name}</Title>
                  {bundle.autoRenew && (
                    <Badge color='grape' variant='light' size='sm'>
                      Auto-Renew
                    </Badge>
                  )}
                </Group>
                <Text size='sm' c='dimmed'>
                  {getDurationText(bundle)}
                </Text>
              </Stack>
              <UnstyledButton onClick={handleRemoveClick} style={{ flexShrink: 0 }}>
                <FontAwesomeIcon icon={faTrash} color='gray' />
              </UnstyledButton>
            </Group>

            <Divider />

            <Text fw={700} size='lg'>
              ${bundle.price.toFixed(2)} — All Locations Bundle
            </Text>
          </Stack>
        </Flex>
      </Paper>

      {/* Full-screen image modal */}
      <Modal
        opened={imageExpanded}
        onClose={() => setImageExpanded(false)}
        size='90%'
        centered
        padding={0}
        withCloseButton={false}
        styles={{
          body: {
            height: '90vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            overflow: 'hidden',
            backgroundColor: 'black',
          },
          content: {
            height: '90vh',
            maxHeight: '90vh',
            overflow: 'hidden',
            backgroundColor: 'black',
          },
          inner: { padding: 0 },
        }}
      >
        <div style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'black' }}>
          <ActionIcon
            variant='filled'
            radius='xl'
            onClick={() => setImageExpanded(false)}
            style={{
              position: 'absolute',
              top: 16,
              right: 16,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              color: 'white',
              zIndex: 1,
            }}
          >
            <FontAwesomeIcon icon={faXmark} />
          </ActionIcon>
          <div style={{ width: '100%', height: '100%', maxWidth: 'calc(90vh * 16/9)', maxHeight: '90vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Image
              src={bundle.imageUrl}
              alt={bundle.name}
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
          </div>
        </div>
      </Modal>
    </>
  )
}

export default AllLocationsBundleCard
